import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Users, Handshake, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const content = {
  es: {
    suptitle: '¿Por que es importante embarcarse en este proceso?',
    title: 'Innovacion Abierta',
    philosophyLabel: 'Nuestra filosofia',
    philosophyTitle: 'Innovacion Abierta',
    p1: 'El termino de innovacion abierta (tambien llamado open innovation) se atribuye a Henry Chesbrough, director ejecutivo del Centro de Open Innovation de la Universidad de Berkeley, a traves de su libro publicado en 2003: "Open Innovation – The new imperative for Creating and Profiting from Technology".',
    p2: 'La innovacion abierta surge como un nuevo paradigma de gestion que concibe la innovacion como un sistema abierto, donde agentes internos y externos participan en el proceso de innovar y mejoran las posibilidades competitivas de la organizacion. No es tanto un reto tecnologico, sino mas bien un cambio sustancial de actitud y de proceso en la organizacion.',
    p3: 'Las claves de la innovacion abierta son la apertura, la colaboracion y la busqueda de la creatividad. Las ideas no son unicamente generadas en el interior, sino que pueden ser generadas de forma colaborativa entre diferentes organizaciones o pueden ser importadas desde organizaciones externas, gestionando los conflictos potenciales.',
    p4: 'Las innovaciones y tecnologias de la organizacion pueden ser compradas, vendidas, licenciadas, prestadas y re-inventadas antes de llegar al mercado tradicional y desde cualquier publico con el que esta interactua.',
    p5: 'Se trata por tanto de un profundo cambio del paradigma imperante en la organizacion que le permite ser mas rapida que la competencia, mas competitiva y aportar mayor valor añadido. Segun diversos estudios, las organizaciones comprometidas con la innovacion abierta trabajan con una red de partners mucho mas extensa y colaboran tanto hacia arriba como hacia abajo en la cadena de valor.',
    keys: [
      { icon: <Lightbulb className="w-6 h-6" />, title: 'Apertura', desc: 'Las ideas fluyen dentro y fuera de la organizacion, rompiendo barreras tradicionales.' },
      { icon: <Handshake className="w-6 h-6" />, title: 'Colaboracion', desc: 'Agentes internos y externos participan activamente en el proceso de innovar.' },
      { icon: <Users className="w-6 h-6" />, title: 'Creatividad', desc: 'Se busca la creatividad desde multiples fuentes, no solo desde el equipo interno.' },
      { icon: <Rocket className="w-6 h-6" />, title: 'Competitividad', desc: 'Permite ser mas rapido y aportar mayor valor añadido que la competencia.' },
    ],
    cta: 'Explorar desafios',
  },
  en: {
    suptitle: 'Why is it important to embark on this process?',
    title: 'Open Innovation',
    philosophyLabel: 'Our philosophy',
    philosophyTitle: 'Open Innovation',
    p1: 'The term open innovation is attributed to Henry Chesbrough, executive director of the Center for Open Innovation at the University of Berkeley, through his book published in 2003: "Open Innovation – The new imperative for Creating and Profiting from Technology".',
    p2: 'Open innovation emerges as a new management paradigm that conceives innovation as an open system, where internal and external agents participate in the innovation process and improve the competitive possibilities of the organization. It is not so much a technological challenge, but rather a substantial change in attitude and process within the organization.',
    p3: 'The keys to open innovation are openness, collaboration, and the pursuit of creativity. Ideas are not only generated internally, but can be generated collaboratively between different organizations or imported from external organizations, managing potential conflicts.',
    p4: 'The innovations and technologies of the organization can be bought, sold, licensed, lent, and reinvented before reaching the traditional market and from any audience with which it interacts.',
    p5: 'It is therefore a profound change in the prevailing paradigm of the organization that allows it to be faster than the competition, more competitive, and provide greater added value. According to various studies, organizations committed to open innovation work with a much more extensive network of partners and collaborate both upstream and downstream in the value chain.',
    keys: [
      { icon: <Lightbulb className="w-6 h-6" />, title: 'Openness', desc: 'Ideas flow in and out of the organization, breaking traditional barriers.' },
      { icon: <Handshake className="w-6 h-6" />, title: 'Collaboration', desc: 'Internal and external agents actively participate in the innovation process.' },
      { icon: <Users className="w-6 h-6" />, title: 'Creativity', desc: 'Creativity is sought from multiple sources, not just the internal team.' },
      { icon: <Rocket className="w-6 h-6" />, title: 'Competitiveness', desc: 'Allows being faster and providing greater added value than the competition.' },
    ],
    cta: 'Explore challenges',
  },
};

const OpenInnovation = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 lg:py-28" style={{ backgroundColor: '#6949b6' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 to-transparent"></div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-purple-200 text-sm font-medium uppercase tracking-widest mb-4">{t.suptitle}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white" data-testid="oi-title">
            {t.title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest mb-2" style={{ color: '#6949b6' }}>{t.philosophyLabel}</p>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t.philosophyTitle}</h2>
          <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
            <p>{t.p1}</p>
            <p>{t.p2}</p>
            <p>{t.p3}</p>
            <p>{t.p4}</p>
            <p>{t.p5}</p>
          </div>
        </div>
      </section>

      {/* Key Concepts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.keys.map((item, i) => (
              <Card key={i} className="border-2 border-gray-100 hover:border-purple-200 transition-all">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#f3f0ff', color: '#6949b6' }}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="text-center">
          <Button asChild size="lg" className="text-white text-lg px-8 py-6" style={{ backgroundColor: '#6949b6' }}>
            <Link to="/programs">
              {t.cta} <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OpenInnovation;
